/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import PropTypes from 'prop-types';
import { cursor as cursorPropTypes } from '@instructure/ui-prop-types';
import { textDirectionContextConsumer } from '@instructure/ui-i18n';
import { ThemeablePropTypes } from '@instructure/emotion';
const propTypes = {
  as: PropTypes.elementType,
  elementRef: PropTypes.func,
  display: PropTypes.oneOf(['auto', 'inline', 'block', 'inline-block', 'flex', 'inline-flex']),
  overflowX: PropTypes.oneOf(['auto', 'hidden', 'visible']),
  overflowY: PropTypes.oneOf(['auto', 'hidden', 'visible']),
  margin: PropTypes.string,
  padding: PropTypes.string,
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  maxHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  maxWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  minHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  minWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  children: PropTypes.node,
  textAlign: PropTypes.oneOf(['start', 'center', 'end']),
  borderWidth: ThemeablePropTypes.borderWidth,
  borderRadius: ThemeablePropTypes.borderRadius,
  borderColor: PropTypes.string,
  background: PropTypes.oneOf(['transparent', 'primary', 'secondary', 'primary-inverse', 'brand', 'info', 'alert', 'success', 'danger', 'warning']),
  shadow: ThemeablePropTypes.shadow,
  stacking: ThemeablePropTypes.stacking,
  cursor: cursorPropTypes,
  position: PropTypes.oneOf(['static', 'absolute', 'relative', 'sticky', 'fixed']),
  insetInlineStart: PropTypes.string,
  insetInlineEnd: PropTypes.string,
  insetBlockStart: PropTypes.string,
  insetBlockEnd: PropTypes.string,
  withFocusOutline: PropTypes.bool,
  focusPosition: PropTypes.oneOf(['offset', 'inset']),
  focusColor: PropTypes.oneOf(['info', 'inverse', 'success', 'danger']),
  shouldAnimateFocus: PropTypes.bool,
  withVisualDebug: PropTypes.bool,
  dir: PropTypes.oneOf(Object.values(textDirectionContextConsumer.DIRECTION)),
  overscrollBehavior: PropTypes.oneOf(['auto', 'contain', 'none']),
  focusRingBorderRadius: PropTypes.string,
  focusWithin: PropTypes.bool
};

// This variable will be attached as static property on the `View` component
// so we don't rely on the `PropTypes` validators for our internal logic.
// This means on prod builds the consuming applications can safely delete propTypes.
const allowedProps = ['as', 'background', 'borderColor', 'borderRadius', 'borderWidth', 'children', 'cursor', 'dir', 'display', 'elementRef', 'focusColor', 'focusPosition', 'height', 'insetBlockEnd', 'insetBlockStart', 'insetInlineEnd', 'insetInlineStart', 'margin', 'maxHeight', 'maxWidth', 'minHeight', 'minWidth', 'overflowX', 'overflowY', 'overscrollBehavior', 'padding', 'position', 'shadow', 'shouldAnimateFocus', 'stacking', 'textAlign', 'width', 'withFocusOutline', 'withVisualDebug', 'focusRingBorderRadius', 'focusWithin'];
export { propTypes, allowedProps };